import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import { Database, Loader2, Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useDataPlan, type DataPlanRow } from "@/hooks/useDataPlan";
import { askAccountMail } from "@/lib/accountMail";
import { PLANS } from "@/content/dataPlans";

// The reader's Data plan on the account page (step 2, 29 Oct 2026). With no paid plan it shows the Free plan
// and the month's downloads left (30 Oct 2026: before, a free reader saw no Data plan at all).
const day = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

// Stripe's billing page, a renewal to pay, or a cancel undone (5 Nov 2026), from the data-billing function; the
// first two leave for Stripe's own page and come back to the account page.
async function billing(action: "portal" | "pay" | "keep") {
  const { data, error } = await supabase.functions.invoke("data-billing", { body: { action } });
  if (error || !data?.success) {
    let e = data?.error;
    try { e = e || (await (error as any)?.context?.json?.())?.error; } catch { /* keep the default */ }
    return { ok: false as const, error: (e as string) || "Please try again." };
  }
  return { ok: true as const, url: data.url as string | undefined, renewsOn: data.renewsOn as string | undefined };
}

function UnpaidPlanCard({ row }: { row: DataPlanRow }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState<"" | "pay" | "portal">("");
  const name = row.plan === "professional" ? "Professional" : "Individual";
  const go = async (action: "pay" | "portal") => {
    setBusy(action);
    const r = await billing(action);
    if (r.ok && r.url) { window.location.href = r.url; return; }
    setBusy("");
    toast({ title: "Could not open the payment page", description: r.ok ? "Please try again." : r.error, variant: "destructive" });
  };
  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-primary/40 bg-primary/10">
          <Database className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-foreground">Data plan: {name}, renewal not paid</h4>
          <p className="text-xs text-muted-foreground">{row.period === "annual" ? "Yearly" : "Monthly"} · the full record opens again once the renewal is paid</p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Calendar className="h-4 w-4" />
        <span>
          {row.provider === "stripe"
            ? `The renewal on ${day(row.current_period_end!)} did not go through. You can pay it now, with this card or another one.`
            : `The renewal on ${day(row.current_period_end!)} did not go through. Write to info@shikshannivesh.com and we will sort it out.`}
        </span>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {row.provider === "stripe" && (
          <>
            <button onClick={() => go("pay")} disabled={!!busy} className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-60">
              {busy === "pay" && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Pay the renewal
            </button>
            <Button variant="outline" size="sm" onClick={() => go("portal")} disabled={!!busy} className="text-muted-foreground">
              {busy === "portal" && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Change the card
            </Button>
          </>
        )}
        <Link to="/dashboard?tab=account#invoices" className="text-xs text-muted-foreground underline underline-offset-4">Invoices</Link>
      </div>
    </Card>
  );
}

function FreePlanCard() {
  const [left, setLeft] = useState<{ left: number; limit: number } | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const t = (await supabase.auth.getSession()).data.session?.access_token;
        if (!t) return;
        const r = await fetch("/api/data/downloads", { headers: { Authorization: `Bearer ${t}` } });
        const b = await r.json().catch(() => null);
        if (active && r.ok && typeof b?.left === "number" && typeof b?.limit === "number") setLeft({ left: b.left, limit: b.limit });
      } catch { /* the line stays out */ }
    })();
    return () => { active = false; };
  }, []);
  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-primary/40 bg-primary/10">
          <Database className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-foreground">Data plan: Free</h4>
          <p className="text-xs text-muted-foreground">{PLANS.find((p) => p.id === "free")!.includes.slice(0, 3).join(" · ")}</p>
        </div>
      </div>
      {left && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{left.left} of {left.limit} downloads left this month</span>
        </div>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Link to="/data/developers#pricing" className="rounded-md border border-[var(--sn-border-strong)] px-3 py-1.5 text-xs font-medium text-foreground hover:border-primary">See the plans</Link>
      </div>
    </Card>
  );
}

export function DataPlanCard({ user }: { user: User }) {
  const { current, unpaid, loading, reload } = useDataPlan(user);
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState<"" | "portal" | "keep">("");
  // a plan on the card: the worker sends "your plan is live" (or "cancelled") once, if it has not yet
  const planKey = current ? `${current.id}:${current.cancel_at_period_end}:${current.status}:${!!current.current_period_end}` : "";
  useEffect(() => {
    if (!planKey) return;
    supabase.auth.getSession().then(({ data }) => askAccountMail(data.session?.access_token)).catch(() => {});
  }, [planKey]);
  if (loading) return null;
  if (!current) return unpaid ? <UnpaidPlanCard row={unpaid} /> : <FreePlanCard />;

  const name = current.plan === "professional" ? "Professional" : "Individual";
  const ending = current.cancel_at_period_end || current.status === "cancelled";
  const waiting = !current.current_period_end;
  const paidOnce = current.billing === "once";
  const renewSoon = paidOnce && !!current.current_period_end && new Date(current.current_period_end).getTime() - Date.now() < 30 * 86400_000;
  const opened = waiting && !!current.provisional_until && new Date(current.provisional_until).getTime() > Date.now();

  const cancel = async () => {
    const msg = waiting
      ? "Cancel your Data plan? Nothing has been charged yet, so it ends now."
      : `Cancel your Data plan? There will be no further charges, and it keeps working until ${day(current.current_period_end!)}.`;
    if (!confirm(msg)) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("cancel-data-subscription", { body: {} });
    setBusy(false);
    if (error || !data?.success) {
      let e = data?.error;
      try { e = e || (await (error as any)?.context?.json?.())?.error; } catch { /* keep the default */ }
      toast({ title: "Could not cancel", description: e || "Please try again.", variant: "destructive" });
      return;
    }
    toast({ title: "Cancelled", description: data.accessUntil ? `Your plan keeps working until ${day(data.accessUntil)}.` : "Your plan has ended. Nothing was charged." });
    reload();
  };

  const byCard = current.provider === "stripe";
  const manage = async () => {
    setOpening("portal");
    const r = await billing("portal");
    if (r.ok && r.url) { window.location.href = r.url; return; }
    setOpening("");
    toast({ title: "Could not open billing", description: r.ok ? "Please try again." : r.error, variant: "destructive" });
  };
  const keep = async () => {
    setOpening("keep");
    const r = await billing("keep");
    setOpening("");
    if (!r.ok) { toast({ title: "Could not keep the plan", description: r.error, variant: "destructive" }); return; }
    toast({ title: "Your plan stays", description: r.renewsOn ? `It renews on ${day(r.renewsOn)} as before.` : "It renews as before." });
    reload();
  };

  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-primary/40 bg-primary/10">
          <Database className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-foreground">Data plan: {name}</h4>
          <p className="text-xs text-muted-foreground">
            {current.period === "annual" ? "Yearly" : "Monthly"} · {waiting
              ? (opened ? "full record on every company page · 10 downloads a month until the first payment clears" : "opens when Cashfree takes the first payment")
              : "full record on every company page · unlimited downloads"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Calendar className="h-4 w-4" />
        <span>
          {waiting ? (current.billing === "bank" ? "First payment: Cashfree takes it once your bank has set up the mandate, usually within a week" : "First payment: Cashfree takes it the day after you approved the mandate")
            : paidOnce ? `Paid for the year, until ${day(current.current_period_end!)}. It does not renew by itself.`
            : ending ? `Ends on ${day(current.current_period_end!)}. No further charges.`
            : `Renews on ${day(current.current_period_end!)}`}
        </span>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {renewSoon && (
          <Link to="/data/developers?renew=1#pricing" className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110">Renew for another year</Link>
        )}
        {ending && byCard && current.status === "active" && !waiting && (
          <button onClick={keep} disabled={!!opening} className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-60">
            {opening === "keep" && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Keep my plan
          </button>
        )}
        {byCard && (
          <Button variant="outline" size="sm" onClick={manage} disabled={!!opening} className="text-muted-foreground">
            {opening === "portal" && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Manage billing
          </Button>
        )}
        {!ending && !paidOnce && (
          <Button variant="outline" size="sm" onClick={cancel} disabled={busy} className="text-muted-foreground">
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Cancel the plan
          </Button>
        )}
        <Link to="/dashboard?tab=account#invoices" className="text-xs text-muted-foreground underline underline-offset-4">Invoices</Link>
        <Link to="/data/developers#pricing" className="text-xs text-muted-foreground underline underline-offset-4">What the plans include</Link>
      </div>
    </Card>
  );
}
